import React, { useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import {
  Users,
  UserPlus,
  UserMinus,
  Plus,
  PencilSimple,
  Trash,
  EyeSlash,
  CheckCircle,
  User,
  Shield,
} from "@phosphor-icons/react";

import CustomText from "../../common/general/CustomText/CustomText";
import { useGetRecentActivityQuery, AuditEventType, AuditEvent } from "../../../graphql/generated/graphql";

const getEventIcon = (type: AuditEventType) => {
  switch (type) {
    case AuditEventType.CourseCreated:
    case AuditEventType.DegreeCreated:
      return Plus;
    case AuditEventType.CourseUpdated:
    case AuditEventType.DegreeUpdated:
      return PencilSimple;
    case AuditEventType.CourseDeleted:
    case AuditEventType.DegreeDeleted:
      return Trash;
    case AuditEventType.CoursePublished:
    case AuditEventType.CourseUnpublished:
      return EyeSlash;
    case AuditEventType.CourseEnrollmentAdded:
    case AuditEventType.DegreeEnrollmentAdded:
    case AuditEventType.UserEnrollmentAdded:
      return UserPlus;
    case AuditEventType.CourseEnrollmentRemoved:
    case AuditEventType.DegreeEnrollmentRemoved:
      return UserMinus;
    case AuditEventType.CourseInvitationAdded:
    case AuditEventType.DegreeInvitationAdded:
    case AuditEventType.UserInvitationAdded:
      return User;
    case AuditEventType.UserInvitationRemoved:
      return UserMinus;
    case AuditEventType.UserCreated:
      return Users;
    case AuditEventType.UserRolesChanged:
      return Shield;
    case AuditEventType.CourseAssignedToFaculty:
      return UserPlus;
    case AuditEventType.CourseRemovedFromFaculty:
      return UserMinus;
    default:
      return CheckCircle;
  }
};

const getEventColor = (type: AuditEventType) => {
  switch (type) {
    case AuditEventType.CourseCreated:
    case AuditEventType.DegreeCreated:
      return "#22C55E";
    case AuditEventType.CourseUpdated:
    case AuditEventType.DegreeUpdated:
      return "#1A8E9D";
    case AuditEventType.CourseDeleted:
    case AuditEventType.DegreeDeleted:
      return "#EF4444";
    case AuditEventType.CoursePublished:
      return "#3B82F6";
    case AuditEventType.CourseUnpublished:
      return "#667085";
    case AuditEventType.CourseEnrollmentAdded:
    case AuditEventType.DegreeEnrollmentAdded:
    case AuditEventType.UserEnrollmentAdded:
      return "#913DAF";
    case AuditEventType.CourseEnrollmentRemoved:
    case AuditEventType.DegreeEnrollmentRemoved:
      return "#F97316";
    case AuditEventType.CourseInvitationAdded:
    case AuditEventType.DegreeInvitationAdded:
    case AuditEventType.UserInvitationAdded:
      return "#913DAF";
    case AuditEventType.UserInvitationRemoved:
      return "#F97316";
    case AuditEventType.UserCreated:
      return "#22C55E";
    case AuditEventType.UserRolesChanged:
      return "#1A8E9D";
    case AuditEventType.CourseAssignedToFaculty:
      return "#913DAF";
    case AuditEventType.CourseRemovedFromFaculty:
      return "#F97316";
    default:
      return "#667085";
  }
};

const formatRoleChanges = (event: AuditEvent) => {
  const { diffKeys, diffBefore, diffAfter } = event;
  const changes: string[] = [];

  const rolesIndex = diffKeys.findIndex((key) => key === "hasRoleRoles");

  if (rolesIndex !== -1) {
    const beforeRoles = diffBefore[rolesIndex] ? JSON.parse(diffBefore[rolesIndex]) : [];
    const afterRoles = diffAfter[rolesIndex] ? JSON.parse(diffAfter[rolesIndex]) : [];

    const extractRoleName = (role: any): string => {
      if (typeof role === "string") return role;
      if (role && typeof role === "object") {
        if (Array.isArray(role)) {
          return role.map((r) => r.name || r.role || r.roleName || "Unknown").join(", ");
        }
        return role.name || role.role || role.roleName || "Unknown";
      }
      return String(role);
    };

    const beforeRoleNames = beforeRoles.map(extractRoleName).filter(Boolean);
    const afterRoleNames = afterRoles.map(extractRoleName).filter(Boolean);

    const addedRoles = afterRoleNames.filter((role: string) => !beforeRoleNames.includes(role));
    if (addedRoles.length > 0) {
      changes.push(`added roles: ${addedRoles.join(", ")}`);
    }

    const removedRoles = beforeRoleNames.filter((role: string) => !afterRoleNames.includes(role));
    if (removedRoles.length > 0) {
      changes.push(`removed roles: ${removedRoles.join(", ")}`);
    }
  }

  return changes.length > 0 ? changes.join(", ") : "roles changed";
};

const formatActivityText = (event: AuditEvent) => {
  const { type, actor, subjectCourse, subjectDegree, subjectUser } = event;
  const actorName = actor?.name || "Deleted User";
  const subjectCourseName = subjectCourse?.name || "Course";
  const subjectDegreeName = subjectDegree?.name || "Degree";
  const subjectUserName = subjectUser?.name || "Deleted User";

  switch (type) {
    case AuditEventType.CourseCreated:
      return `${actorName} created course "${subjectCourseName}"`;
    case AuditEventType.CourseUpdated:
      return `${actorName} updated course "${subjectCourseName}"`;
    case AuditEventType.CourseDeleted:
      return `${actorName} deleted course "${subjectCourseName}"`;
    case AuditEventType.CoursePublished:
      return `Course "${subjectCourseName}" was published by ${actorName}`;
    case AuditEventType.CourseUnpublished:
      return `Course "${subjectCourseName}" was unpublished by ${actorName}`;
    case AuditEventType.CourseEnrollmentAdded:
      return `${actorName} enrolled in course "${subjectCourseName}"`;
    case AuditEventType.CourseEnrollmentRemoved:
      return `${subjectUserName} was removed from course "${subjectCourseName}" by ${actorName}`;
    case AuditEventType.CourseInvitationAdded:
      return `${subjectUserName} was invited to course "${subjectCourseName}" by ${actorName}`;
    case AuditEventType.CourseAssignedToFaculty:
      return `${actorName} assigned course "${subjectCourseName}" to faculty "${subjectUserName}"`;
    case AuditEventType.CourseRemovedFromFaculty:
      return `${actorName} removed course "${subjectCourseName}" from faculty "${subjectUserName}"`;
    case AuditEventType.DegreeCreated:
      return `${actorName} created degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeUpdated:
      return `${actorName} updated degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeDeleted:
      return `${actorName} deleted degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeEnrollmentAdded:
      return `${actorName} enrolled in degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeEnrollmentRemoved:
      return `${subjectUserName} was removed from degree "${subjectDegreeName} " by ${actorName}`;
    case AuditEventType.DegreeInvitationAdded:
      return `${subjectUserName} was invited to degree "${subjectDegreeName}" by ${actorName}`;
    case AuditEventType.UserInvitationRemoved:
      return `${subjectUserName} was removed from invitation by ${actorName}`;
    case AuditEventType.UserCreated:
      return `New user "${subjectUserName}" was created by ${actorName}`;
    case AuditEventType.UserRolesChanged:
      const roleChanges = formatRoleChanges(event);
      return `${actorName} changed roles for "${subjectUserName}": ${roleChanges}`;
    case AuditEventType.UserEnrollmentAdded:
      return `${actorName} was enrolled`;
    case AuditEventType.UserInvitationAdded:
      return `${actorName} invited user "${subjectUserName}"`;
    default:
      return `${actorName} performed activity "${type}"`;
  }
};

const formatTimeAgo = (dateString: string) => {
  const now = new Date();
  const eventDate = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - eventDate.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "Just now";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return eventDate.toLocaleDateString();
    }
  }
};

const AdminRecentActivity: React.FC = () => {
  const { data: recentActivityData, startPolling, stopPolling, refetch } = useGetRecentActivityQuery();
  const recentActivity = recentActivityData?.getRecentActivity?.data;

  useEffect(() => {
    startPolling(10000); // Poll every 10 seconds
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  if (!recentActivity || recentActivity.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.activityHeader}>
        <CustomText text="Recent Activity" size="l" weight="bold" style={styles.activityTitle} />
      </View>
      <View style={styles.activityList}>
        {recentActivity.slice(0, 25).map((event, index) => {
          const IconComponent = getEventIcon(event.type);
          const iconColor = getEventColor(event.type);
          const isLastItem = index === Math.min(recentActivity.length - 1, 4);

          return (
            <View key={event.id} style={[styles.activityItem, isLastItem && styles.lastActivityItem]}>
              <View style={[styles.activityIcon, { backgroundColor: iconColor }]}>
                <IconComponent size={16} color="#FFFFFF" />
              </View>
              <View style={styles.activityContent}>
                <CustomText
                  text={formatActivityText(event as AuditEvent)}
                  size="s"
                  weight="medium"
                  style={styles.activityText}
                  useTranslationText={false}
                />
                <CustomText
                  text={formatTimeAgo(event.occurredAt)}
                  size="xs"
                  weight="normal"
                  style={styles.activityTime}
                  useTranslationText={false}
                />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FCFCFD",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#EAECF0",
    marginBottom: 32,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  activityHeader: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  activityTitle: {
    color: "#101828",
  },
  activityList: {
    padding: 0,
  },
  activityItem: {
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#F2F4F7",
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  lastActivityItem: {
    borderBottomWidth: 0,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    color: "#667085",
  },
  activityTime: {
    color: "#667085",
  },
});

export default AdminRecentActivity;
